import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listMembers, setCustomerAssignments } from '../api/client';
import type { TenantMember, CustomerAssignedMember } from '../api/client';
import { useAuth } from '../context/AuthContext';

interface ManageAssignmentsModalProps {
  customerId: number;
  customerName: string;
  currentAssignments: CustomerAssignedMember[];
  onClose: () => void;
  onSaved: (updated: CustomerAssignedMember[]) => void;
}

function memberDisplayName(m: TenantMember): string {
  return m.name || m.email || m.user_id;
}

export default function ManageAssignmentsModal({
  customerId,
  customerName,
  currentAssignments,
  onClose,
  onSaved,
}: ManageAssignmentsModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [members, setMembers] = useState<TenantMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(currentAssignments.map((m) => m.user_id)),
  );
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.tenant_id) return;
    setLoadingMembers(true);
    listMembers(user.tenant_id)
      .then((res) => setMembers(res.members.filter((m) => m.status !== 'revoked')))
      .catch(() => setError(t('assignments.loadError', 'Failed to load members')))
      .finally(() => setLoadingMembers(false));
  }, [user?.tenant_id, t]);

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter(
      (m) =>
        (m.name || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q),
    );
  }, [members, search]);

  const toggle = (userId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!user?.tenant_id) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await setCustomerAssignments(customerId, Array.from(selectedIds));
      onSaved(updated.assigned_members);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || t('assignments.saveError', 'Failed to save assignments'));
    } finally {
      setSaving(false);
    }
  };

  const roleLabel = (role: string) => {
    if (role === 'account_executive') return 'AE';
    if (role === 'admin') return t('settings.roleAdmin', 'Admin');
    if (role === 'owner') return t('settings.roleOwner', 'Owner');
    return t('settings.roleMember', 'Member');
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 flex flex-col max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {t('assignments.modalTitle', 'Manage Assignments')}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5 truncate max-w-[280px]">{customerName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors text-lg leading-none"
          >
            ✕
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-3 pb-2 shrink-0">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('assignments.searchPlaceholder', 'Search members…')}
            className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            data-testid="search-members-input"
          />
        </div>

        {/* Member list */}
        <div className="flex-1 overflow-y-auto px-5 pb-2">
          {loadingMembers ? (
            <p className="text-sm text-gray-400 py-4 text-center">{t('common.loading')}</p>
          ) : filteredMembers.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">
              {t('assignments.noMembersFound', 'No members found')}
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {filteredMembers.map((m) => (
                <li key={m.user_id}>
                  <label className="flex items-center gap-3 py-2.5 cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(m.user_id)}
                      onChange={() => toggle(m.user_id)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      data-testid={`member-option-${m.user_id}`}
                    />
                    {m.picture ? (
                      <img
                        src={m.picture}
                        alt={memberDisplayName(m)}
                        className="w-7 h-7 rounded-full shrink-0"
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-bold shrink-0">
                        {memberDisplayName(m).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{memberDisplayName(m)}</p>
                      {m.email && m.name && (
                        <p className="text-xs text-gray-500 truncate">{m.email}</p>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">{roleLabel(m.role)}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 shrink-0">
          {error && (
            <p className="text-sm text-red-600 mb-3">{error}</p>
          )}
          {selectedIds.size === 0 && !loadingMembers && (
            <p className="text-xs text-amber-600 mb-2">
              {t('assignments.adminFallback', 'No members selected — only Admins will receive notifications for this customer.')}
            </p>
          )}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="save-assignments-button"
            >
              {saving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
